import { useEffect } from 'react';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import { createStore, createActionsHook, createStateHook } from '@atlassian/react-sweet-state';
import { useSpaStatePageReady } from '@atlassian/jira-spa-state-controller/src/components/page-ready-state/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';

const store = createStore({
	name: 'priority-manager',
	initialState: { tier: 1, nonCriticalStalled: false },
	actions: {
		setTier:
			(tier: number) =>
			({ setState, getState }) =>
				setState({
					tier: Math.max(getState().tier, tier),
				}),
		markNonCriticalStalled:
			() =>
			({ setState, getState }) => {
				if (getState().tier === loadingPriority.nonCritical) {
					setState({ nonCriticalStalled: true });
				}
			},
		markNonCriticalTimedOut:
			() =>
			({ setState, getState }) => {
				if (getState().tier === loadingPriority.nonCritical) {
					log.safeErrorWithoutCustomerData(
						'PriorityManager',
						'Hard timeout of non-critical tier, advancing to background priority',
					);
					setState({ tier: loadingPriority.background });
				}
			},
	},
});

export const usePriorityActions = createActionsHook(store);
export const useCurrentPriority = createStateHook(store, { selector: (state) => state.tier });
export const useIsNonCriticalStalled = createStateHook(store, {
	selector: (state) => state.nonCriticalStalled,
});

const NON_CRITICAL_STALL_TIMEOUT = 5_000;
const NON_CRITICAL_HARD_TIMEOUT = 25_000;

/**
 * defines loading priorities(tiers) for the application
 */
export const loadingPriority = {
	// tier 0 - "early scripts" is outside of this component reach and responsibility
	/**
	 * Tier 1 - initial loading phase, everything is critical
	 */
	critical: 1,
	/**
	 * Tier 2 - app is ready, but some work is still in progress
	 * This is the phase where we can start loading non-critical resources
	 */
	nonCritical: 2,
	/**
	 * Tier 3 - background phase, all work is done, we can load background resources
	 * This is the phase where we can start loading background resources
	 */
	background: 3,
} as const;

/**
 * @deprecated - this is POC component to manage loading priorities in SPA.
 * This component connects SPA and UFO states in a way we can get something useful today
 * Implementation will be changed in the future.
 */
export function PriorityManager() {
	const [{ isReady }] = useSpaStatePageReady();
	const { setTier, markNonCriticalStalled, markNonCriticalTimedOut } = usePriorityActions();
	useEffect(() => {
		if (isReady) {
			performance.mark('priority:non-critical-phase');
			setTier(loadingPriority.nonCritical);

			if (fg('jfp-magma-fix-hydration-completion')) {
				const stallTimer = setTimeout(markNonCriticalStalled, NON_CRITICAL_STALL_TIMEOUT);
				const timeoutTimer = setTimeout(markNonCriticalTimedOut, NON_CRITICAL_HARD_TIMEOUT);
				return () => {
					clearTimeout(stallTimer);
					clearTimeout(timeoutTimer);
				};
			}

			const tm = setTimeout(() => {
				setTier(loadingPriority.background);
			}, NON_CRITICAL_STALL_TIMEOUT);
			return () => {
				clearTimeout(tm);
			};
		}
	}, [isReady, setTier, markNonCriticalStalled, markNonCriticalTimedOut]);

	useEffect(() => {
		if (typeof window !== 'undefined') {
			const onPageReady = () => {
				performance.mark('priority:background-phase');
				setTier(loadingPriority.background);
			};
			window.addEventListener('UFO_FINISH_INTERACTION', onPageReady);
			return () => {
				window.removeEventListener('UFO_FINISH_INTERACTION', onPageReady);
			};
		}
	}, [setTier]);
	return null;
}
